import { notification } from "antd";
import { useEffect, useState } from "react";
import {
    getRouteFindAllByMarketId,
    getSchedulerNextVisitDates,
} from "tm-shared/api";

interface UseAvailableDatesResult {
    dates: { [roleSlug: string]: string[] };
    roles: string[];
    isLoading: boolean;
}

export function useAvailableDates(
    marketId: string | undefined,
): UseAvailableDatesResult {
    const [dates, setDates] = useState<{ [roleSlug: string]: string[] }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<string[]>([]);

    useEffect(() => {
        // Не робимо запит якщо маркет ще не вибраний
        if (!marketId) {
            setDates({});
            setRoles([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const fetchDates = async () => {
            try {
                setIsLoading(true);

                const marketRoutes = await getRouteFindAllByMarketId({
                    marketId,
                });

                if (cancelled) return;

                const roleSlugs = marketRoutes.result.map(
                    (route) => route.roleSlug,
                );

                setRoles(roleSlugs);

                const datePromises = marketRoutes.result.map(async (route) => {
                    try {
                        const result = await getSchedulerNextVisitDates({
                            marketId,
                            routeId: route.uuid,
                        });

                        return {
                            roleSlug: route.roleSlug,
                            dates: result.result,
                        };
                    } catch (error) {
                        console.error(
                            `Failed to fetch dates for role ${route.roleSlug}:`,
                            error,
                        );
                        return { roleSlug: route.roleSlug, dates: [] };
                    }
                });

                const results = await Promise.all(datePromises);

                if (cancelled) return;

                const datesMap = results.reduce(
                    (acc, { roleSlug, dates }) => {
                        acc[roleSlug] = dates;
                        return acc;
                    },
                    {} as { [roleSlug: string]: string[] },
                );

                setDates(datesMap);
            } catch (error) {
                if (!cancelled) {
                    console.error("Failed to fetch available dates:", error);
                    notification.error({
                        message: "Помилка завантаження доступних дат",
                    });
                    setDates({});
                    setRoles([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchDates();

        return () => {
            cancelled = true;
        };
    }, [marketId]);

    return { dates, roles, isLoading };
}
